import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle,
  Clock,
  ExternalLink,
  Play,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CoverageMentionSchema,
  CoverageSchema,
} from '@/services/types/coverage-types';

interface MentionsTableProps {
  mentions: CoverageMentionSchema[];
  onMentionSelect: (mentionId: string) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  availableCoverages?: CoverageSchema[];
}

export const MentionsTable = ({
  mentions,
  onMentionSelect,
  sortField,
  sortDirection,
  onSort,
  availableCoverages = [],
}: MentionsTableProps) => {
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-gray-400" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-gray-600" />
    ) : (
      <ArrowDown className="h-3 w-3 text-gray-600" />
    );
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'PENDING':
        return <AlertCircle className="h-3 w-3 text-yellow-600" />;
      case 'PROCESSING':
        return <Play className="h-3 w-3 text-blue-600" />;
      case 'READY':
        return <CheckCircle className="h-3 w-3 text-green-600" />;
      case 'ACCEPTED':
        return <CheckCircle className="h-3 w-3 text-emerald-600" />;
      case 'REJECTED':
        return <X className="h-3 w-3 text-red-600" />;
      case 'ERROR':
        return <AlertTriangle className="h-3 w-3 text-red-600" />;
      default:
        return <AlertCircle className="h-3 w-3 text-gray-500" />;
    }
  };

  const getCategoryBadge = (category: string) => {
    if (category === 'FIRST_PARTY') return '1P';
    if (category === 'FIRST_PARTY_HEALTH') return '1P';
    if (category === 'THIRD_PARTY') return '3P';
    return category.charAt(0);
  };

  const getLinkedCoverage = (coverageId: string | null) => {
    if (!coverageId) return null;
    return availableCoverages.find((c) => c.id === coverageId);
  };

  const formatTime = (dateString: string | undefined) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return '—';
    }
  };

  if (mentions.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">No mentions match the current filters</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50 h-8">
            <TableHead className="w-[50px] py-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort('id')}
                className="p-0 h-auto text-xs font-semibold hover:bg-transparent"
              >
                ID {getSortIcon('id')}
              </Button>
            </TableHead>
            <TableHead className="w-[45px] py-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort('created_at')}
                className="p-0 h-auto text-xs font-semibold hover:bg-transparent"
              >
                <Clock className="h-3 w-3" />
                {getSortIcon('created_at')}
              </Button>
            </TableHead>
            <TableHead className="w-[40px] py-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort('status')}
                className="p-0 h-auto text-xs font-semibold hover:bg-transparent"
              >
                Status {getSortIcon('status')}
              </Button>
            </TableHead>
            <TableHead className="w-[140px] py-1">
              <span className="text-xs font-semibold">Coverage Details</span>
            </TableHead>
            <TableHead className="py-1">
              <span className="text-xs font-semibold">Context</span>
            </TableHead>
            <TableHead className="w-[120px] py-1">
              <span className="text-xs font-semibold">Linked To</span>
            </TableHead>
            <TableHead className="w-[50px] py-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSort('confidence')}
                className="p-0 h-auto text-xs font-semibold hover:bg-transparent"
              >
                Score {getSortIcon('confidence')}
              </Button>
            </TableHead>
            <TableHead className="w-[50px] py-1">
              <span className="text-xs font-semibold">Src</span>
            </TableHead>
            <TableHead className="w-[35px] py-1" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {mentions.map((mention) => (
            <TableRow
              key={mention.id}
              className="hover:bg-gray-50 cursor-pointer border-b h-12"
              onClick={() => onMentionSelect(mention.id)}
            >
              <TableCell className="font-mono text-xs py-1 px-2">
                {mention.id.slice(-4)}
              </TableCell>
              <TableCell className="py-1 px-2">
                <span className="text-xs text-gray-500">
                  {formatTime((mention as any).created_at)}
                </span>
              </TableCell>
              <TableCell className="py-1 px-2">
                <div className="flex items-center justify-center">
                  {getStatusIcon(mention.status)}
                </div>
              </TableCell>
              <TableCell className="py-1 px-2">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    {mention.extraction?.carrier?.name && (
                      <span className="text-xs font-medium text-gray-900 truncate max-w-[100px]">
                        {mention.extraction.carrier.name}
                      </span>
                    )}
                    {mention.extraction?.category && (
                      <Badge
                        variant="outline"
                        className="text-xs px-1 py-0 h-4"
                      >
                        {getCategoryBadge(mention.extraction.category)}
                      </Badge>
                    )}
                  </div>
                  {mention.extraction?.policy_identifiers &&
                    mention.extraction.policy_identifiers.length > 0 && (
                      <span className="text-xs font-mono text-gray-500 truncate max-w-[120px]">
                        {mention.extraction.policy_identifiers[0].identifier_value.slice(
                          0,
                          10,
                        )}
                        {mention.extraction.policy_identifiers[0]
                          .identifier_value.length > 10
                          ? '...'
                          : ''}
                      </span>
                    )}
                </div>
              </TableCell>
              <TableCell className="py-1 px-2">
                {mention.extraction?.context ? (
                  <div className="text-xs text-gray-600 leading-relaxed">
                    <div className="line-clamp-3">
                      {mention.extraction.context}
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-400 text-xs">—</span>
                )}
              </TableCell>
              <TableCell className="py-1 px-2">
                {mention.coverage_id ? (
                  (() => {
                    const linkedCoverage = availableCoverages.find(
                      (c) => c.id === mention.coverage_id,
                    );
                    return linkedCoverage ? (
                      <div className="flex items-center gap-1">
                        <ExternalLink className="h-3 w-3 text-green-600 flex-shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs font-medium text-green-700 truncate max-w-[80px]">
                            {linkedCoverage.carrier?.name || 'Unknown'}
                          </span>
                          {linkedCoverage.named_insured &&
                            linkedCoverage.named_insured.length > 0 && (
                              <span className="text-xs text-gray-400 truncate max-w-[80px]">
                                {linkedCoverage.named_insured[0].split(' ')[0]}
                              </span>
                            )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <ExternalLink className="h-3 w-3 text-orange-500" />
                        <span className="text-xs text-orange-600">Missing</span>
                      </div>
                    );
                  })()
                ) : (
                  <span className="text-gray-400 text-xs">—</span>
                )}
              </TableCell>
              <TableCell className="py-1 px-2">
                <div className="flex flex-col gap-0.5">
                  {mention.extraction?.confidence !== undefined && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs">
                        {Math.round(mention.extraction.confidence * 100)}
                      </span>
                      {mention.extraction.confidence > 0.8 && (
                        <div className="w-1 h-1 bg-green-500 rounded-full" />
                      )}
                    </div>
                  )}
                  {mention.validation?.coverage_specificity_score !==
                    undefined && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">
                        S
                        {Math.round(
                          mention.validation.coverage_specificity_score * 100,
                        )}
                      </span>
                      {mention.validation.coverage_specificity_score > 0.7 && (
                        <div className="w-1 h-1 bg-blue-500 rounded-full" />
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="py-1 px-2">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-gray-600">
                    {mention.artifact_type.charAt(0)}
                  </span>
                  <span className="text-xs font-mono text-gray-400">
                    {mention.artifact_id.slice(-3)}
                  </span>
                </div>
              </TableCell>
              <TableCell className="py-1 px-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMentionSelect(mention.id);
                  }}
                  className="h-5 w-5 p-0"
                >
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
